import React, { useEffect, useState, useCallback } from 'react';
import superAdminApi from '../../utils/superAdminApi';
import toast from 'react-hot-toast';
import { FiPlus, FiEdit2, FiTrash2, FiPackage, FiAlertTriangle } from 'react-icons/fi';

// Fallback only — the real list is the "productCategory" group in Cloth &
// Style Management, so new kinds (e.g. Caps / Topi) can be added there.
const FALLBACK_CATEGORIES = ['Clothing', 'Footwear', 'Accessories', 'Thread', 'Fabric', 'Buttons', 'Zippers', 'Lining', 'Other'];

// Same quick sizes the app offered before products moved to this panel.
const QUICK_SIZES = ['S', 'M', 'L', 'XL', 'XXL'];

const emptyForm = { name: '', category: 'Accessories', price: '', purchasePrice: '', stockQty: '', minAlertQty: 5, hasSizes: false, variants: [] };

export default function SuperAdminInventory() {
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [restockProduct, setRestockProduct] = useState(null);
  const [restockQty, setRestockQty] = useState('');
  const [restockSize, setRestockSize] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);

  useEffect(() => {
    superAdminApi.get('/config-options', { params: { group: 'productCategory' } })
      .then(r => {
        const vals = (r.data.options || []).filter(o => o.isActive !== false).map(o => o.value);
        if (vals.length) setCategories(vals);
      }).catch(() => {});
  }, []);

  useEffect(() => {
    superAdminApi.get('/shops').then(r => {
      setShops(r.data.shops);
      if (r.data.shops.length > 0) setShopId(r.data.shops[0]._id);
      else setLoading(false);
    }).catch(() => toast.error('Failed to load shops'));
  }, []);

  const fetchProducts = useCallback(async () => {
    if (!shopId) return;
    setLoading(true);
    try {
      const r = await superAdminApi.get('/inventory/products', { params: { shopId } });
      setProducts(r.data.products);
    } catch { toast.error('Failed to load products'); }
    setLoading(false);
  }, [shopId]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openAdd = () => { setForm(emptyForm); setEditProduct(null); setShowModal(true); };
  const openEdit = (p) => {
    const variants = (p.variants || []).map(v => ({ size: v.size, stockQty: v.stockQty ?? 0 }));
    setForm({ name: p.name, category: p.category, price: p.price, purchasePrice: p.purchasePrice, stockQty: p.stockQty,
      minAlertQty: p.minAlertQty, hasSizes: variants.length > 0, variants });
    setEditProduct(p);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.price) return toast.error('Name and price are required');
    const { hasSizes, ...rest } = form;
    let payload;
    if (hasSizes) {
      const variants = form.variants.map(v => ({ size: `${v.size}`.trim(), stockQty: parseInt(v.stockQty) || 0 }));
      if (variants.length === 0) return toast.error('Add at least one size, or turn sizes off');
      if (variants.some(v => !v.size)) return toast.error('Fill in all size names, or remove empty rows');
      if (new Set(variants.map(v => v.size.toLowerCase())).size !== variants.length) return toast.error('Duplicate size names are not allowed');
      payload = { ...rest, variants, stockQty: variants.reduce((a, v) => a + v.stockQty, 0) };
    } else {
      payload = { ...rest, variants: [] };
    }
    setSaving(true);
    try {
      if (editProduct) {
        await superAdminApi.put(`/inventory/products/${editProduct._id}`, payload);
        toast.success('Product updated');
      } else {
        await superAdminApi.post('/inventory/products', { ...payload, shopId });
        toast.success('Product added');
      }
      setShowModal(false);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    }
    setSaving(false);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from inventory?`)) return;
    try {
      await superAdminApi.delete(`/inventory/products/${id}`);
      toast.success('Product removed');
      fetchProducts();
    } catch { toast.error('Delete failed'); }
  };

  const handleRestock = async () => {
    if (!restockQty || parseInt(restockQty) <= 0) return toast.error('Enter valid quantity');
    const sized = (restockProduct.variants || []).length > 0;
    if (sized && !restockSize) return toast.error('Select a size to restock');
    try {
      await superAdminApi.put(`/inventory/products/${restockProduct._id}/restock`,
        { quantity: restockQty, ...(sized ? { size: restockSize } : {}) });
      toast.success(`Restocked ${restockQty} units`);
      setShowRestockModal(false);
      setRestockQty('');
      setRestockSize('');
      fetchProducts();
    } catch { toast.error('Restock failed'); }
  };

  const lowStockCount = products.filter(p => p.stockQty <= p.minAlertQty).length;

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label className="form-label" style={{ marginBottom: 0 }}>Shop</label>
          <select className="form-control" style={{ width: 260 }} value={shopId} onChange={e => setShopId(e.target.value)}>
            {shops.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>
        </div>
      </div>

      {lowStockCount > 0 && (
        <div className="alert alert-danger" style={{ marginBottom: 16 }}>
          <FiAlertTriangle size={16} />
          <span><strong>{lowStockCount} product(s)</strong> are running low on stock!</span>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#64748b' }}>{products.length} products</div>
        <button className="btn btn-accent" onClick={openAdd} disabled={!shopId}><FiPlus size={15} /> Add Product</button>
      </div>

      <div className="card">
        {loading ? <div className="loading"><div className="spinner"></div></div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Min Alert</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.length === 0 && (
                  <tr><td colSpan={7}>
                    <div className="empty-state">
                      <div className="empty-state-icon"><FiPackage size={40} /></div>
                      <h3>No products yet</h3>
                      <p>Add inventory items for this shop</p>
                    </div>
                  </td></tr>
                )}
                {products.map(p => {
                  const isLow = p.stockQty <= p.minAlertQty;
                  return (
                    <tr key={p._id}>
                      <td style={{ fontWeight: 600 }}>{p.name}</td>
                      <td><span className="badge badge-gray">{p.category}</span></td>
                      <td>SAR {p.price?.toFixed(2)}</td>
                      <td>
                        <span style={{ fontWeight: 700, color: isLow ? '#ef4444' : '#10b981', fontSize: 15 }}>
                          {p.stockQty}
                        </span>
                        {(p.variants || []).length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                            {p.variants.map(v => (
                              <span key={v.size} className="badge badge-gray" style={{ fontSize: 11 }}>{v.size}: {v.stockQty}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td style={{ color: '#94a3b8' }}>{p.minAlertQty}</td>
                      <td>
                        {isLow
                          ? <span className="badge badge-danger">⚠️ Low Stock</span>
                          : <span className="badge badge-success">In Stock</span>}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn btn-outline btn-sm" onClick={() => { setRestockProduct(p); setRestockSize(''); setShowRestockModal(true); }}>
                            + Restock
                          </button>
                          <button className="btn btn-outline btn-sm btn-icon" onClick={() => openEdit(p)}>
                            <FiEdit2 size={13} />
                          </button>
                          <button className="btn btn-outline btn-sm btn-icon"
                            style={{ color: '#ef4444', borderColor: '#fecaca' }}
                            onClick={() => handleDelete(p._id, p.name)}>
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editProduct ? 'Edit Product' : 'Add Product'}</h3>
              <button className="btn btn-outline btn-sm btn-icon" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Product Name <span className="required">*</span></label>
                <input className="form-control" placeholder="e.g. Leather Belt"
                  value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select className="form-control" value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}>
                    {[...new Set([...categories, form.category].filter(Boolean))].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Sell Price (SAR) <span className="required">*</span></label>
                  <input type="number" className="form-control" placeholder="0.00"
                    value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Purchase Price (SAR)</label>
                  <input type="number" className="form-control" placeholder="0.00"
                    value={form.purchasePrice} onChange={e => setForm({ ...form, purchasePrice: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Stock Quantity</label>
                  {form.hasSizes ? (
                    <input className="form-control" disabled
                      value={`${form.variants.reduce((a, v) => a + (parseInt(v.stockQty) || 0), 0)} (total of sizes)`} />
                  ) : (
                    <input type="number" className="form-control" placeholder="0"
                      value={form.stockQty} onChange={e => setForm({ ...form, stockQty: e.target.value })} />
                  )}
                </div>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.hasSizes}
                    onChange={e => setForm({ ...form, hasSizes: e.target.checked,
                      variants: e.target.checked && form.variants.length === 0 ? [{ size: '', stockQty: '' }] : form.variants })} />
                  This product has sizes (Small / Medium / Large — stock per size)
                </label>
              </div>
              {form.hasSizes && (
                <div className="form-group">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                    {QUICK_SIZES.map(sz => (
                      <button key={sz} type="button" className="btn btn-outline btn-sm"
                        disabled={form.variants.some(v => `${v.size}`.trim().toLowerCase() === sz.toLowerCase())}
                        onClick={() => setForm({ ...form, variants: [...form.variants.filter(v => `${v.size}`.trim()), { size: sz, stockQty: '' }] })}>
                        + {sz}
                      </button>
                    ))}
                  </div>
                  {form.variants.map((v, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                      <input className="form-control" style={{ flex: 2 }} placeholder="Size, e.g. M"
                        value={v.size} onChange={e => setForm({ ...form, variants: form.variants.map((x, j) => j === i ? { ...x, size: e.target.value } : x) })} />
                      <input type="number" className="form-control" style={{ flex: 1 }} placeholder="Stock"
                        value={v.stockQty} onChange={e => setForm({ ...form, variants: form.variants.map((x, j) => j === i ? { ...x, stockQty: e.target.value } : x) })} />
                      <button type="button" className="btn btn-outline btn-sm btn-icon" style={{ color: '#ef4444', borderColor: '#fecaca' }}
                        onClick={() => setForm({ ...form, variants: form.variants.filter((_, j) => j !== i) })}>✕</button>
                    </div>
                  ))}
                  <button type="button" className="btn btn-outline btn-sm"
                    onClick={() => setForm({ ...form, variants: [...form.variants, { size: '', stockQty: '' }] })}>
                    <FiPlus size={13} /> Add Size
                  </button>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Min Alert Qty</label>
                <input type="number" className="form-control" placeholder="5"
                  value={form.minAlertQty} onChange={e => setForm({ ...form, minAlertQty: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-accent" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : (editProduct ? 'Update' : 'Add Product')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRestockModal && restockProduct && (
        <div className="modal-overlay" onClick={() => setShowRestockModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">📦 Restock: {restockProduct.name}</h3>
              <button className="btn btn-outline btn-sm btn-icon" onClick={() => setShowRestockModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 13 }}>
                Current Stock: <strong>{restockProduct.stockQty}</strong> units
                {(restockProduct.variants || []).length > 0 && (
                  <span> ({restockProduct.variants.map(v => `${v.size}: ${v.stockQty}`).join(', ')})</span>
                )}
              </div>
              {(restockProduct.variants || []).length > 0 && (
                <div className="form-group">
                  <label className="form-label">Size <span className="required">*</span></label>
                  <select className="form-control" value={restockSize} onChange={e => setRestockSize(e.target.value)}>
                    <option value="">Select size</option>
                    {restockProduct.variants.map(v => <option key={v.size} value={v.size}>{v.size} (now {v.stockQty})</option>)}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Add Quantity</label>
                <input type="number" className="form-control" placeholder="e.g. 50"
                  value={restockQty} onChange={e => setRestockQty(e.target.value)} autoFocus />
              </div>
              {restockQty && (
                <div style={{ color: '#10b981', fontSize: 13 }}>
                  New total: <strong>{restockProduct.stockQty + parseInt(restockQty || 0)}</strong> units
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowRestockModal(false)}>Cancel</button>
              <button className="btn btn-success" onClick={handleRestock}>Add Stock</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
